/**
 * 企业应用 API
 */
import { request, getCurrentTenantId } from './api';

function getApiPath(path: string): string {
  const tenantId = getCurrentTenantId();
  const prefix = tenantId && tenantId !== 'default' ? "/" + tenantId : "";
  return prefix + path;
}

// --- 类型定义 ---

export interface AppHistory {
  v: string;
  d: string;
  n: string;
}

export interface AppVersion {
  ver: string;
  file_key: string;
  file_type: string;
  file_size: number;
  note?: string;
  pub: string;
  created_at: string;
  current?: boolean;
}

export interface AppItem {
  id: number | string;
  cat: string;
  category_id?: string | number;
  name: string;
  ver: string;
  version?: string;
  badge: '' | 'new' | 'hot' | 'upd';
  grad: string;
  icon: string;
  thumbnail?: string;
  screenshot1?: string;  // 预览截图1
  screenshot2?: string;  // 预览截图2
  pub: string;
  dept: string;
  av: string;
  avc: string;
  visibility?: 'public' | 'dept';
  source: string;
  freq: string;
  time: string;
  uses: number;
  desc: string;
  intro: string;
  feats: string[];
  features?: string;
  note?: string;
  hist: AppHistory[];
  file_key?: string;
  file_type?: string;
  file_size?: number;
  file_name?: string;
  entry_file?: string;
  app_kind?: 'static_site' | 'ai_project' | 'source_bundle';
  instruction_entry?: string;
  share_token?: string;
  share_enabled?: boolean;
  allow_download?: boolean;
  data_api?: string;
  api_url?: string;
  data_refresh?: number;
  refresh?: string;
  icon_choice?: { icon: string; tone: string } | string; // 可能是对象或JSON字符串
}

export function isSourcePackage(app: AppItem): boolean {
  return app.file_type === 'zip' && !app.entry_file;
}

export function isAIProject(app: AppItem): boolean {
  return app.app_kind === 'ai_project';
}

export interface AppCategory {
  id: string;
  name: string;
  grad: string;
  icon: string;
}

export interface ZipHTMLFile {
  path: string;
  name: string;
  size: number;
  is_dev_entry: boolean;
}

export interface UploadResult {
  file_key: string;
  file_type: string;
  file_size: number;
  file_name: string;
  entry_file?: string;
  html_files?: ZipHTMLFile[];
  html_sample: string;
  is_source_package?: boolean;
  source_sha256: string;
  app_kind: 'static_site' | 'ai_project' | 'source_bundle';
  instruction_entry?: string;
}

export interface PublishRequest {
  id?: string;
  name: string;
  cat: string;
  desc?: string;
  feats?: string[];
  note?: string;
  visibility?: 'public' | 'dept';
  file_key: string;
  file_type: string;
  file_size: number;
  entry_file?: string;
  data_api?: string;
  data_refresh?: number;
  thumbnail?: string;
  source_filename?: string;
  source_sha256?: string;
  app_kind?: 'static_site' | 'ai_project' | 'source_bundle';
  instruction_entry?: string;
  icon_choice?: string;
}

export interface ShareInfo {
  share_token: string;
  share_enabled: boolean;
  allow_download: boolean;
  workbuddy_can_use: boolean;
  workbuddy_can_download: boolean;
  workbuddy_supported: boolean;
  current_version_id?: string;
}

export interface WorkbuddyHandoff {
  handoff_id: string;
  execution_id: string;
  app_id: string;
  app_version_id: string;
  version: string;
  host: 'workbuddy';
  bundle_url: string;
  bundle_sha256: string;
  expires_at: string;
  prompt: string;
}

// --- API ---

export async function listApps(): Promise<AppItem[]> {
  return request<AppItem[]>('/apps');
}

export async function getApp(id: string): Promise<AppItem> {
  return request<AppItem>(`/apps/${id}`);
}

export async function listCategories(): Promise<AppCategory[]> {
  return request<AppCategory[]>('/apps/cats');
}

// --- 文件上传 ---

export async function uploadAppFile(file: File): Promise<UploadResult> {
  const formData = new FormData();
  formData.append('file', file);
  
  const token = localStorage.getItem('eb-token');
  const res = await fetch(getApiPath('/api/apps/upload'), {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: '上传失败' }));
    const errorMsg = err.message || err.error || '上传失败';
    
    let friendlyMsg = errorMsg;
    if (errorMsg.includes('无效的 ZIP') || errorMsg.includes('invalid zip') || errorMsg.includes('zip: not a valid')) {
      friendlyMsg = '压缩包无法读取，请重新压缩后上传';
    } else if (errorMsg.includes('文件过大') || errorMsg.includes('too large')) {
      friendlyMsg = '文件超过大小限制（最大 300MB）';
    } else if (errorMsg.includes('格式') || errorMsg.includes('format') || errorMsg.includes('不支持')) {
      friendlyMsg = '文件格式不支持，请上传 .html 或 .zip 文件';
    } else if (errorMsg.includes('网络') || errorMsg.includes('network') || errorMsg.includes('timeout')) {
      friendlyMsg = '网络连接失败，请检查网络后重试';
    }
    
    throw new Error(friendlyMsg);
  }

  const json = await res.json();
  return json.data;
}

export async function publishApp(data: PublishRequest): Promise<{ id: string; ver: string; share_token?: string }> {
  return request('/apps/publish', {
    method: 'POST',
    body: data,
  });
}

// --- 查看/下载 ---

export function getAppViewUrl(id: string): string {
  // Token 不再通过 URL 传递，改用 fetch + Authorization header
  // 返回基础 URL，调用方需要使用 fetchAppView 函数获取内容
  return getApiPath(`/api/apps/${id}/view`);
}

export function getAppDownloadUrl(id: string): string {
  return getApiPath(`/api/apps/${id}/download`);
}

export function getAppHtmlUrl(id: string): string {
  // Token 不再通过 URL 传递
  return getApiPath(`/api/apps/${id}/view`);
}

// 安全的应用查看函数：使用 Authorization header 而非 URL 参数
export async function fetchAppView(id: string): Promise<string> {
  const token = localStorage.getItem('eb-token');
  const res = await fetch(getApiPath(`/api/apps/${id}/view`), {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new Error('获取应用内容失败');
  }
  return res.text();
}

export function getShareViewUrl(shareToken: string): string {
  return `/api/apps/share/${shareToken}/view`;
}

export function getShareDownloadUrl(shareToken: string): string {
  return `/api/apps/share/${shareToken}/download`;
}

// --- 分享管理 ---

export async function getShareInfo(id: string): Promise<ShareInfo> {
  return request<ShareInfo>(`/apps/${id}/share`);
}

export async function createWorkbuddyHandoff(id: string, idempotencyKey: string): Promise<WorkbuddyHandoff> {
  return request<WorkbuddyHandoff>(`/apps/${id}/handoffs`, {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey },
    body: { host: 'workbuddy' },
  });
}

export async function revokeWorkbuddyHandoff(id: string, handoffId: string): Promise<void> {
  await request(`/apps/${id}/handoffs/${handoffId}`, { method: 'DELETE' });
}

export async function updateShare(id: string, data: {
  share_enabled?: boolean;
  allow_download?: boolean;
  reset_token?: boolean;
}): Promise<ShareInfo> {
  return request<ShareInfo>(`/apps/${id}/share`, {
    method: 'PUT',
    body: data,
  });
}

// --- 版本管理 ---

export async function listVersions(id: string): Promise<AppVersion[]> {
  return request<AppVersion[]>(`/apps/${id}/versions`);
}

export async function rollbackVersion(id: string, ver: string): Promise<{ message: string; ver: string }> {
  return request(`/apps/${id}/rollback`, {
    method: 'POST',
    body: { ver },
  });
}

export function getVersionDownloadUrl(id: string, ver: string): string {
  return getApiPath(`/api/apps/${id}/versions/${ver}/download`);
}

// --- 管理（管理员） ---

export async function createApp(data: Partial<AppItem> | FormData): Promise<AppItem> {
  if (data instanceof FormData) {
    const token = localStorage.getItem('eb-token');
    const res = await fetch(getApiPath('/api/apps'), {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: data,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: '创建失败' }));
      throw new Error(err.message || err.error || '创建失败');
    }
    const json = await res.json();
    return json.data;
  }
  return request<AppItem>('/apps', {
    method: 'POST',
    body: data,
  });
}

export async function updateApp(id: string, updates: Partial<AppItem>): Promise<void> {
  await request(`/apps/${id}`, {
    method: 'PUT',
    body: updates,
  });
}

export async function deleteApp(id: string): Promise<void> {
  await request(`/apps/${id}`, { method: 'DELETE' });
}

// --- 分类管理（管理员） ---

export async function createCategory(cat: Partial<AppCategory>): Promise<AppCategory> {
  return request<AppCategory>('/apps/cats', {
    method: 'POST',
    body: cat,
  });
}

export async function updateCategory(id: string, updates: Partial<AppCategory>): Promise<void> {
  await request(`/apps/cats/${id}`, {
    method: 'PUT',
    body: updates,
  });
}

export async function deleteCategory(id: string): Promise<void> {
  await request(`/apps/cats/${id}`, { method: 'DELETE' });
}

// --- 版本更新 ---

export async function updateAppVersion(id: number | string, formData: FormData): Promise<{ ver: string }> {
  const token = localStorage.getItem('eb-token');
  const res = await fetch(getApiPath(`/api/apps/${id}/versions`), {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: '版本更新失败' }));
    throw new Error(err.message || err.error || '版本更新失败');
  }

  const json = await res.json();
  return json.data;
}
